import { ColumnsType } from "antd/es/table";
import {
  AssetInfo,
  RatingTag,
  ReportRating,
} from "../../../agent/AgentContent/AIXRay/Rating";

import { BaseTable } from "../../TokenSpotlights/Crypto/components";
import { I18nUtil } from "@utils/i18n";
import { MarketCapColumn } from "@/src/components/table/dashboard/columns";
import { Token } from "@/src/features/api/openData";
import Translations from "@translate/components/Home/Report/Crypto/CryptoReport";
import { useGoToDetail } from "@/src/hooks/useGoToDetail";
import { useIsPc } from "@/src/hooks";
import { useTokensRankingInfiniteScroll } from "@/src/hooks/useTokensRankingInfiniteScroll";

const I18n = I18nUtil.createScoped(Translations);

type ExtendedToken = Token & {
  latest_rating?: {
    level?: {
      tokenomics?: string;
      final?: string;
      fundamental?: string;
      momentum?: string;
    };
    version?: string;
  };
};

export const CryptoReport = () => {
  const [isPC] = useIsPc();
  const { data } = useTokensRankingInfiniteScroll({
    queryParams: {
      limit: 5,
      type: "REPORT_360",
    },
  });
  const columns: ColumnsType<ExtendedToken> = [
    {
      title: I18n.t("Token"),
      dataIndex: "symbol",
      key: "token",
      width: isPC ? 140 : 100,
      fixed: "left",
      align: "left",
      render: (_, record: ExtendedToken) => (
        <AssetInfo
          symbol={record?.symbol ?? ""}
          name={record?.name ?? ""}
          icon={record?.logo ?? ""}
        />
      ),
    },
    {
      title: I18n.t("Change"),
      dataIndex: "market_data",
      key: "change",
      width: isPC ? 114 : 80,
      align: "left",
      render: (marketData: any) => {
        const change24h = marketData?.price_change_percentage?.find(
          (item: any) => item.time_period === "24h",
        )?.percentage;
        if (change24h === undefined) {
          return "--";
        }
        const isPositive = Number(change24h) >= 0;
        return (
          <span
            className={`${isPositive ? "text-content_accent1_primary" : "text-content_accent2_primary"}`}
          >
            {isPositive ? "+" : ""}
            {Number(change24h).toFixed(2)}%
          </span>
        );
      },
    },
    {
      title: I18n.t("Market Cap"),
      dataIndex: "market_data",
      key: "marketCap",
      width: isPC ? 114 : 88,
      align: "left",
      render: (_value: string, record: Token) => {
        return (
          <MarketCapColumn
            record={{
              market_cap: record.market_data?.market_cap?.toString() ?? "0",
            }}
          />
        );
      },
    },
    {
      title: I18n.t("Fundamental"),
      dataIndex: "latest_rating",
      key: "valuation",
      width: 100,
      align: "left",
      render: (rating: ExtendedToken["latest_rating"]) => (
        <RatingTag rating={rating?.level?.fundamental ?? ""} />
      ),
    },
    {
      title: I18n.t("Tokenomics"),
      dataIndex: "latest_rating",
      key: "growth",
      width: 100,
      align: "left",
      render: (rating: ExtendedToken["latest_rating"]) => (
        <RatingTag rating={rating?.level?.tokenomics ?? ""} />
      ),
    },
    {
      title: I18n.t("Momentum"),
      dataIndex: "latest_rating",
      key: "profitability",
      width: 100,
      align: "left",
      render: (rating: ExtendedToken["latest_rating"]) => (
        <RatingTag rating={rating?.level?.momentum ?? ""} />
      ),
    },
    {
      title: I18n.t("Rating"),
      dataIndex: "latest_rating",
      key: "rating",
      fixed: "right",
      align: "center",
      render: (
        rating: ExtendedToken["latest_rating"],
        record: ExtendedToken,
      ) => (
        <ReportRating
          rating={rating?.level?.final ?? ""}
          symbol={record?.symbol ?? ""}
          id={record?.id ?? ""}
        />
      ),
      width: isPC ? 100 : 60,
    },
  ];
  const { onDetail, isLoading } = useGoToDetail("crypto");
  const onRow = (record: ExtendedToken) => {
    onDetail(record);
  };
  return (
    <BaseTable
      columns={columns}
      dataSource={data ?? []}
      loading={false}
      onRow={onRow}
      isSearchLoading={isLoading}
    />
  );
};
